import * as React from 'react';
import { Chip, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';

interface UserTagRecalculationStatusProps {
    tag: UserTagDefinitionDto;
    compact?: boolean;
}

const formatNumber = (value: number) => value.toLocaleString('en-US');

export default function UserTagRecalculationStatus({ tag, compact = false }: UserTagRecalculationStatusProps) {
    const recalculationInfo = tag.tagRecalculationInfo;
    const primaryVariant = compact ? 'body2' : 'body1';
    const secondaryVariant = compact ? 'caption' : 'body2';

    if (recalculationInfo != null) {
        const totalUsersConsidered = Math.max(recalculationInfo.totalUsersConsidered, 0);
        const totalUsersProcessed = Math.min(
            recalculationInfo.totalUsersProcessed,
            totalUsersConsidered > 0 ? totalUsersConsidered : recalculationInfo.totalUsersProcessed,
        );
        const progressValue = totalUsersConsidered > 0
            ? Math.min(100, (totalUsersProcessed / totalUsersConsidered) * 100)
            : 0;
        const progressProps = totalUsersConsidered > 0
            ? { variant: 'determinate' as const, value: progressValue }
            : { variant: 'indeterminate' as const };

        return (
            <Stack gap={compact ? 1 : 1.5}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <CircularProgress size={compact ? 16 : 18} />
                    <Typography variant={primaryVariant}>
                        Recalculating user tag
                    </Typography>
                    <Chip
                        size="small"
                        variant="outlined"
                        label={recalculationInfo.status === 'Pending' ? 'Pending' : 'In progress'}
                    />
                </Stack>
                <LinearProgress {...progressProps} />
                <Typography variant={secondaryVariant} color="text.secondary">
                    {totalUsersConsidered > 0
                        ? `Processed ${formatNumber(totalUsersProcessed)} of ${formatNumber(totalUsersConsidered)} eligible users`
                        : 'Preparing eligible users for recalculation'}
                </Typography>
                <Stack direction="row" gap={2} flexWrap="wrap">
                    <Typography variant={secondaryVariant} color="text.secondary">
                        Received tag: {formatNumber(recalculationInfo.totalUsersReceivedTag)}
                    </Typography>
                    <Typography variant={secondaryVariant} color="text.secondary">
                        Lost tag: {formatNumber(recalculationInfo.totalUsersLostTag)}
                    </Typography>
                </Stack>
            </Stack>
        );
    }

    if (tag.approximateUsersCount == null) {
        return (
            <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={compact ? 16 : 18} />
                <Typography variant={primaryVariant} color="text.secondary">
                    Waiting for recalculation to start
                </Typography>
            </Stack>
        );
    }

    return (
        <Typography variant={primaryVariant} color="text.secondary">
            Approximate users: {formatNumber(tag.approximateUsersCount)}
        </Typography>
    );
}
